<template>
    <BaseModal :title="title" @close="handleModal" :visible="true" size="small" customClass="record__audio__popup">
        <div class="add__note__test__box">
            <h6 class="title">Test Item</h6>
            <div class="addnote__box">
                <label>{{ item?.question?.question ?? item?.question }}</label>
                <div class="tbh__labels__group">
                    <span>{{ formatConsonants(item?.question?.consonant ?? item?.consonant) }}</span>
                </div>
            </div>
        </div>
        <div class="play__audio__box__wrapper">
            <div ref="waveformContainer" class="transcribeWaveform" v-show="audioUrl" style="width: 100%"></div>
            <div class="play__audio__time" v-show="audioUrl">
                <h3 class="time">{{ formattedTime }}</h3>
            </div>
            <div class="play__audio__wave" v-show="!audioUrl">
                <span>{{ unavailableDetail ? RECORDING_UNAVAILABLE_TITLE : 'No Audio' }}</span>
                <p class="recording-unavailable-note" v-if="unavailableDetail">{{ unavailableDetail }}</p>
            </div>
            <div class="mic__play__btn">
                <div class="play__btn" v-show="audioUrl">
                    <img v-if="!isPlaying" class="imgPlay" :src="noPlayIcon"
                        @click="togglePlayback" />
                    <img v-else class="imgPause" :src="pauseIcon"
                        @click="togglePlayback" />
                </div>
            </div>
        </div>
    </BaseModal>
</template>

<script setup>
import { computed, nextTick, ref, watch } from 'vue';
import BaseModal from './BaseModal.vue';
import WaveSurfer from 'wavesurfer.js';
import RegionsPlugin from 'wavesurfer.js/dist/plugins/regions';
import { formatConsonants } from '@/utils/format-helpers';
import { RECORDING_UNAVAILABLE_TITLE, recordingUnavailableDetail } from '@/utils/recording-retention';
import noPlayIcon from '@/assets/images/audio/transcribe/no-play.png';
import pauseIcon from '@/assets/images/audio/transcribe/pause.png';
import api from '@/services/axios';
import { handleError } from '@/utils/handleError';
import { useLoaderStore } from '@/stores/loader';
import { useAuthStore } from '@/stores/auth';

// eslint-disable-next-line
const emit = defineEmits(['close', 'saved']);

// Props from parent component
const props = defineProps({
    item: Object
});

const title = `Play Audio`;

const showModal = ref(true);
const waveformContainer = ref(null);
const wavesurfer = ref(null);
const audioUrl = ref('');
const isPlaying = ref(false);
const timeElapsed = ref(0);
const regions = RegionsPlugin.create();
const audioStartTime = ref(null);
const audioEndTime = ref(null);
const loader = useLoaderStore();

// Set only when this item's audio file has gone: the screen says so instead of
// showing a player with nothing behind it.
const unavailableDetail = computed(() => recordingUnavailableDetail(props.item));

const handleModal = (action = 'close') => {
    // There is no player at all on an item with no recording, expired or never
    // recorded, and closing the modal used to throw on it.
    wavesurfer.value?.pause();

    showModal.value = false;
    emit(action);
};

const formattedTime = computed(() => {
    const min = Math.floor(timeElapsed.value / 60);
    const sec = timeElapsed.value % 60;
    return `${min.toString().padStart(2, '0')}:${sec.toString().padStart(2, '0')}`;
});

// Audio player initialization
const initWaveSurfer = () => {
    if (wavesurfer.value) wavesurfer.value.destroy();

    wavesurfer.value = WaveSurfer.create({
        container: waveformContainer.value,
        barWidth: 3,
        barGap: 1,
        barRadius: 1,
        height: 75,
        normalize: true,
        backend: 'WebAudio',
        blobMimeType: 'audio/webm',
        progressColor: 'rgba(114,92,255,1)',
        waveColor: 'rgba(210,199,254,1)',
        cursorColor: "#4a74a5",
        plugins: [regions]
    });

    wavesurfer.value.load(audioUrl.value);

    wavesurfer.value.on('ready', () => {
        timeElapsed.value = Math.floor(wavesurfer.value.getDuration());
    });

    wavesurfer.value.on('audioprocess', (currentTime) => {
        if (Math.ceil(currentTime) <= Math.ceil(wavesurfer.value.getDuration())) {
            timeElapsed.value = Math.floor(currentTime);
        } else {
            setTimeout(() => {
                wavesurfer.value.stop();
                isPlaying.value = false;
                timeElapsed.value = Math.floor(wavesurfer.value.getDuration());
            }, 500);
        }
    });
    
    wavesurfer.value.on('finish', () => {
        timeElapsed.value = Math.floor(wavesurfer.value.getDuration());
        isPlaying.value = false;
    });

    wavesurfer.value.on('decode', () => {
        if (audioStartTime.value && audioEndTime.value) {
            regions.addRegion({
                start: audioStartTime.value,
                end: audioEndTime.value,
                color: 'rgba(114, 92, 255, 0.50)',
                drag: false,
                resize: false,
            });
        }

        let loop = false

        {
            let activeRegion = null
            regions.on('region-in', () => {
                // activeRegion = region
                isPlaying.value = true
            })
            regions.on('region-out', (region) => {
                if (activeRegion === region) {
                    if (loop) {
                        region.play()
                    } else {
                        activeRegion = null
                        isPlaying.value = false;
                    }                    
                }
            })
            regions.on('region-clicked', (region, e) => {
                e.stopPropagation() // prevent triggering a click on the waveform
                activeRegion = region
                region.play(true);
                region.setOptions({ color: 'rgba(114, 92, 255, 0.50)' })
            })

            // Reset the active region when the user clicks anywhere in the waveform
            wavesurfer.value.on('interaction', () => {
                activeRegion = null
            });
        }
    })
};

// Toggle audio playback
const togglePlayback = () => {
    if (!wavesurfer.value) return;
    isPlaying.value ? wavesurfer.value.pause() : wavesurfer.value.play();
    isPlaying.value = !isPlaying.value;
};

const fetchAudioBlobUrl = async (url) => {
    if (!url) return null;
    
    try {
        loader.show();
        const token = useAuthStore().token;
        const response = await api.get(url, {
            responseType: "blob",
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return URL.createObjectURL(response.data);
    } catch (error) {
        handleError(error);

        return null;
    } finally {
        loader.hide();
    }
};

watch(() => props.item, async (newItem) => {
    audioStartTime.value = newItem.start_time;
    audioEndTime.value = newItem.end_time;
    
    audioUrl.value = await fetchAudioBlobUrl(newItem.audio);
    if (audioUrl.value) {
        nextTick(() => {
            initWaveSurfer();
        });
    }
}, { immediate: true, deep: true })
</script>

<style scoped>
.recording-unavailable-note {
    margin: 6px 0 0;
    font-size: 13px;
    line-height: 18px;
    color: #6B7280;
    text-align: center;
}
</style>
